import { useId } from 'react';
import { emptyMaterialsRow, SERVICE_TEXT_LIMITS, type FieldErrors, type MaterialsRowDraft } from './servicePageDraft';

/**
 * Spec 081a — "Materials & details" rows editor: up to 15 rows of label + value, English and
 * Arabic side by side, with Move up / Move down / Remove per row. Rows left completely blank
 * are dropped on save. Server errors arrive as `materialsDetails[i].label` and the like.
 */
export interface MaterialsRowsEditorProps {
  rows: MaterialsRowDraft[];
  onChange: (rows: MaterialsRowDraft[]) => void;
  errors: FieldErrors;
}

type Cell = keyof MaterialsRowDraft;

const CELLS: { key: Cell; lang: 'en' | 'ar'; name: string; max: number; placeholder: string }[] = [
  { key: 'label', lang: 'en', name: 'Label (English)', max: SERVICE_TEXT_LIMITS.materialsLabel, placeholder: 'e.g. Oil' },
  { key: 'labelAr', lang: 'ar', name: 'Label (Arabic)', max: SERVICE_TEXT_LIMITS.materialsLabel, placeholder: 'مثال: الزيت' },
  { key: 'value', lang: 'en', name: 'Value (English)', max: SERVICE_TEXT_LIMITS.materialsValue, placeholder: 'e.g. Fully synthetic 5W-30' },
  { key: 'valueAr', lang: 'ar', name: 'Value (Arabic)', max: SERVICE_TEXT_LIMITS.materialsValue, placeholder: 'مثال: صناعي بالكامل 5W-30' },
];

export default function MaterialsRowsEditor({ rows, onChange, errors }: MaterialsRowsEditorProps) {
  const id = useId();
  const max = SERVICE_TEXT_LIMITS.materialsRows;
  const full = rows.length >= max;
  const listError = errors.materialsDetails;

  const setCell = (i: number, key: Cell, value: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const removeRow = (i: number) => onChange(rows.filter((_, j) => j !== i));
  const moveRow = (i: number, delta: number) => {
    const to = i + delta;
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  };

  return (
    <fieldset className="svc-list svc-mat" data-testid="service-materials-editor">
      <legend className="svc-list__legend">Materials &amp; details</legend>
      <p className="v2-hint">Brands, grades or specs the customer cares about, e.g. Oil → Fully synthetic 5W-30.</p>
      {rows.length === 0 && <p className="svc-list__empty">No rows yet.</p>}
      {rows.length > 0 && (
        <div className="svc-mat__head" aria-hidden="true">
          <span>English</span>
          <span>Arabic</span>
        </div>
      )}
      <ol className="svc-list__items">
        {rows.map((row, i) => {
          const rowErrors = CELLS
            .map((c) => ({ c, msg: errors[`materialsDetails[${i}].${c.key}`] }))
            .filter((x) => x.msg);
          const rowError = errors[`materialsDetails[${i}]`];
          return (
            <li key={i} className="svc-mat__row" data-testid={`service-materials-row-${i}`}>
              <div className="svc-mat__grid">
                {CELLS.map((c) => {
                  const cellError = errors[`materialsDetails[${i}].${c.key}`];
                  const inputId = `${id}-${i}-${c.key}`;
                  return (
                    <input
                      key={c.key}
                      id={inputId}
                      type="text"
                      className={`svc-mat__cell svc-mat__cell--${c.key}`}
                      value={row[c.key]}
                      dir={c.lang === 'ar' ? 'rtl' : 'ltr'}
                      lang={c.lang}
                      maxLength={c.max}
                      placeholder={i === 0 ? c.placeholder : undefined}
                      aria-label={`Materials row ${i + 1} — ${c.name}`}
                      aria-invalid={cellError ? true : undefined}
                      aria-describedby={cellError ? `${id}-${i}-errors` : undefined}
                      onChange={(e) => setCell(i, c.key, e.target.value)}
                    />
                  );
                })}
              </div>
              <div className="svc-row-actions">
                <button type="button" className="svc-icon-btn" disabled={i === 0}
                  aria-label={`Move materials row ${i + 1} up`} title="Move up" onClick={() => moveRow(i, -1)}>↑</button>
                <button type="button" className="svc-icon-btn" disabled={i === rows.length - 1}
                  aria-label={`Move materials row ${i + 1} down`} title="Move down" onClick={() => moveRow(i, 1)}>↓</button>
                <button type="button" className="svc-icon-btn svc-icon-btn--danger"
                  aria-label={`Remove materials row ${i + 1}`} title="Remove" onClick={() => removeRow(i)}>×</button>
              </div>
              {(rowErrors.length > 0 || rowError) && (
                <div id={`${id}-${i}-errors`} className="svc-mat__errors" role="alert">
                  {rowError && <span className="svc-field__error">{rowError}</span>}
                  {rowErrors.map(({ c, msg }) => (
                    <span key={c.key} className="svc-field__error">{c.name}: {msg}</span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="svc-list__footer">
        <button type="button" className="v2-add-btn v2-add-btn-small svc-add-btn" disabled={full}
          onClick={() => { if (!full) onChange([...rows, emptyMaterialsRow()]); }}>
          + Add row
        </button>
        <span className={`svc-field__counter${full ? ' svc-field__counter--limit' : ''}`}>{rows.length}/{max} rows</span>
      </div>
      {listError && <p className="svc-field__error" role="alert">{listError}</p>}
    </fieldset>
  );
}
